<template>
  <Modal :id="'dtplugins-dbox_costs-open_advance'" @cancelled="onCancel">
    <template slot="header"> Open Advance </template>
    <template slot="body">
      <div class="body-container">
        <div
          class="form-group"
          :class="{
            'dt-loading': advanceDataLoading,
          }"
          style="
            max-height: 600px;
            overflow-y: auto;
            padding-right: 32px;
            margin-right: -32px;
          "
        >
          <TextField
            :id="'date'"
            class="m1"
            v-bind="textFieldConfig"
            type="date"
            v-model="openAdvance.date"
            isModel
            required
            showValidationIcons
            :validators="recoupDateValidators"
            :placeholder="$t('recoupment_date')"
            @valuechanged="onTextFieldValueChange"
            @validated="onValidation"
          />
          <Dropdown
            :id="'type'"
            class="m2"
            :options="royalty_accountsTV"
            :selected="
              royalty_accountsTV.find(
                (e) => e.value == openAdvance.royalty_account
              )
            "
            :placeholder="$t('royalty_account')"
            deselect
            box
            showValidationIcons
            required
            immediateValidation
            @selectedChange="(e) => onDropdownChange('royalty_account', e)"
            @validated="(e) => onValidation('royalty_account', e)"
          />
          <Dropdown
            :id="'type'"
            class="m2"
            :options="contractsTV"
            :selected="contractsTV.find((e) => e.value == openAdvance.contract)"
            :placeholder="$t('contract')"
            deselect
            box
            showValidationIcons
            required
            immediateValidation
            @selectedChange="(e) => onDropdownChange('contract', e)"
            @validated="(e) => onValidation('contract', e)"
          />

          <template v-if="openAdvance.contract">
            <TextField
              :id="'amount_orig_curr'"
              class="m1"
              v-bind="textFieldConfig"
              v-model="openAdvance.amount_orig_curr"
              isModel
              :placeholder="
                $t('account_advance') + ' ' + advanceData.cs.currency
              "
              @valuechanged="onTextFieldValueChange"
            />
            <TextField
              :id="'description'"
              class="m1"
              v-bind="textFieldConfig"
              v-model="openAdvance.description"
              isModel
              :placeholder="$t('description')"
              @valuechanged="onTextFieldValueChange"
            />
            <TextField
              :id="'amount'"
              class="m1"
              v-bind="textFieldConfig"
              v-model="openAdvance.amount"
              isModel
              :placeholder="$t('amount') + ' EUR'"
              @valuechanged="onTextFieldValueChange"
            />
          </template>
        </div>
      </div>
    </template>

    <template slot="options">
      <button class="dt-button s6" @click.stop="onCancel">
        {{ $t("cancel") }}
      </button>
      <button
        :class="{ disabled: !isFormValid }"
        :disabled="!isFormValid"
        class="dt-button v1 s6"
        @click.stop="onOkClick"
      >
        {{ $t("account") }}
      </button>
    </template>
  </Modal>
</template>

<script>
import Modal from "@dtplugins/modal/modal";
import InputField from "@plugins/text-field/input-field";
import NTable from "@/plugins/datatable/datatable";
import { DBoxCostService } from "./dbox-costs.service";
import { createTV } from "../../models/text-value.model";
import {
  createFormItemMixin,
  createFormModalMixin,
} from "../form-modal/form-modal";
import { isValidNumber, subscribeSubject } from "../../utility";
import { createValidator } from "../../plugins/text-field";
import { Subject } from "rxjs";
import { isBefore, isEqual } from "date-fns";

export default {
  components: {
    Modal,
    InputField,
    NTable,
  },
  props: ["itemName", "config"],
  mixins: [],
  async mounted() {
    this.advanceDataLoading = true;
    const advanceRes = await DBoxCostService.fetchAdvanceData({
      cost_split_id: this.cost_split_id,
    });
    this.advanceData = advanceRes.data;

    // https://trello.com/c/HZJFjW3P
    // if there is only one contract or royalty account, select it by default
    if (this.advanceData?.related_contracts?.length == 1) {
      this.openAdvance.contract =
        this.advanceData?.related_contracts[0].contract_id;
    }
    if (this.advanceData?.related_royalty_accounts?.length==1) {
      // this.openAdvance.royalty_account =
      //   this.advanceData?.related_royalty_accounts[0].royalty_account_id;
      this.onDropdownChange("royalty_account", {
        value: this.advanceData?.related_royalty_accounts[0].royalty_account_id,
      });
    }

    this.advanceDataLoading = false;

    this.openAdvance = {
      ...this.openAdvance,
      date: this.advanceData?.cs?.date,
      currency: this.advanceData?.cs?.currency,
      amount_orig_curr: this.advanceData?.cs?.amount_orig_curr,
      amount: this.advanceData?.cs?.amount,
      description: this.advanceData?.cs?.description,
    };
  },
  data() {
    return {
      textFieldConfig: {
        height: 42,
        showEditIconOnHover: false,
        box: true,
        immediateValidation: true,
      },
      openAdvance: this.resetOpenAdvance(),
      advanceData: null,
      validationResult: {},

      advanceDataLoading: false,
    };
  },
  computed: {
    isFormValid: function () {
      return Object.values(this.validationResult).every((e) => e.valid);
    },
    cost_split_id: function () {
      return this.$route.query.cost_split_id;
    },
    royalty_accountsTV() {
      return createTV(
        this.advanceData?.royalty_accounts || [],
        "royalty_account_name",
        "royalty_account_id"
      );
    },
    contractsTV() {
      return createTV(
        (this.advanceData?.contracts || []).filter(
          (e) => e.royalty_account_id == this.openAdvance.royalty_account
        ),
        "contract_name",
        "contract_id"
      );
    },
    recoupDateValidators() {
      return [
        createValidator({
          name: "statementAlreadyCalulated",
          validatorFn: this.checkAlreadyCalcStatement,
          errorMessage: "Statement already calculated!",
        }),
      ];
    },
  },
  methods: {
    onOkClick: async function () {
      const cs = this.advanceData.cs;

      const advanceMade = await DBoxCostService.makeAccountAdvance({
        orig_curr: cs.currency,
        cost_group_id: cs.cost_group_id,
        cost_amount_sys_curr: cs.amount,
        cost_amount_orig_curr: cs.amount_orig_curr,
        cost_amount_roy_curr: cs.amount, // ?????
        balance_date: this.openAdvance.date,
        // According to Card: https://trello.com/c/HZJFjW3P
        // Amount Must be Negative of the Amount Entered

        amount: this.openAdvance.amount * -1,

        balance_type: 1,
        amount_sys_curr: this.openAdvance.amount * -1,

        description: cs.description,
        cost_split_id: cs.cost_split_id,
        royalty_account_id: this.openAdvance.royalty_account,
        contract_id: this.openAdvance.contract,
        debit_credit_flag: 1,
        external_id: this.config.primaryKeyValue,
        external_table: this.config.externalTable,
      });
      this.$emit("advance_made", {
        name: "openAdvance",
        kVal: this.cost_split_id,
        value: advanceMade.data.recoupable_txt,
        cost_split_id: this.cost_split_id,
        recoupable_txt: advanceMade.data.recoupable_txt,
      });
    },
    onCancel: function () {
      this.$emit("cancelled", true);
    },
    onValidation: function (name, value) {
      this.$set(this.validationResult, name, value);
    },
    onTextFieldValueChange(name, value) {
      this.openAdvance = {
        ...this.openAdvance,
        [name]: value,
      };
    },
    async onDropdownChange(name, value) {
      const previousValue = this.openAdvance[name];
      this.openAdvance[name] = value.value;

      if (name == "royalty_account") {
        if (previousValue != value.value) {
          this.openAdvance["contract"] = "";
          if (this.contractsTV?.length > 0) {
            this.openAdvance["contract"] = this.contractsTV?.[0]?.value;

            console.log({
              cost_split_id: this.cost_split_id,
              royalty_account_id: value.value,
            });
            const advanceDateRes = await DBoxCostService.fetchAdvanceDate({
              cost_split_id: this.cost_split_id,
              royalty_account_id: value.value,
            });
            console.log({
              "advanceDateRes.data.dates.cost_split_date":
                advanceDateRes.data.dates.cost_split_date,
            });
            this.openAdvance = {
              ...this.openAdvance,
              date:
                advanceDateRes?.data?.dates?.next_stmt_date ||
                advanceDateRes?.data?.dates?.cost_split_date,
              next_stmt_date: advanceDateRes?.data?.dates?.next_stmt_date,
            };
          }
        } 
        else {
        }
      }
    },
    resetOpenAdvance() {
      return {
        royalty_account: null,
        contract: null,
        date: null,
        currency: null,
        amount_orig_curr: null,
        amount: null,
        description: null,
        next_stmt_date: null,
      };
    },
    checkAlreadyCalcStatement(value) {
      console.log({
        "this.openAdvance?.next_stmt_date": this.openAdvance?.next_stmt_date,
      });
      console.log({ value: value });
      if (this.openAdvance?.next_stmt_date) {
        return (
          isBefore(this.openAdvance?.next_stmt_date, value) ||
          isEqual(this.openAdvance?.next_stmt_date, value)
        );
      } else {
        return true;
      }
    },
    //MARK: Methods
  },
};
</script>

<style lang="scss">
.dtplugins-dbox_costs-open_advance {
  .form-group {
    /* padding-right: 32px; */
  }
}
</style>


